import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    projectType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4o0fel0qurnbdguf7vg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          website: '',
          projectType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contacto" ref={ref} className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
          {/* Left Side - Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="bg-purple-600 rounded-3xl p-8 sm:p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
                ¿Listo para Transformar tu SEO?
              </h2>
              <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 leading-relaxed">
                Solicita una auditoría gratuita y descubre cómo podemos llevar tu sitio web al siguiente nivel con nuestra automatización SEO con IA.
              </p>

              <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl sm:text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-base sm:text-lg mb-1">Auditoría SEO Gratuita</h4>
                    <p className="opacity-90 text-sm">Análisis completo de tu sitio web sin compromiso</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl sm:text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-base sm:text-lg mb-1">Respuesta en 24h</h4>
                    <p className="opacity-90 text-sm">Te contactamos en menos de un día hábil</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-xl sm:text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-base sm:text-lg mb-1">Sin Compromiso</h4>
                    <p className="opacity-90 text-sm">Consulta gratuita sin obligación de contratación</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/20 pt-6 sm:pt-8 space-y-3 sm:space-y-4">
                <h4 className="font-bold text-base sm:text-lg mb-4">Información de Contacto</h4>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-lg sm:text-xl"></i>
                  <span className="text-sm sm:text-base">info@seogenia.es</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-lg sm:text-xl"></i>
                  <span className="text-sm sm:text-base">+34 659 357 423</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-map-pin-line text-lg sm:text-xl"></i>
                  <span className="text-sm sm:text-base">Madrid, España</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-time-line text-lg sm:text-xl"></i>
                  <span className="text-sm sm:text-base">Lun - Vie: 9:00 - 18:00</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white"
          >
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Solicita tu Auditoría Gratuita</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Completa el formulario y nos pondremos en contacto contigo</p>

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-4 sm:space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-cyan-400 outline-none transition-colors duration-300 bg-transparent text-sm sm:text-base"
                  placeholder="Tu nombre"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-cyan-400 outline-none transition-colors duration-300 bg-transparent text-sm sm:text-base"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-cyan-400 outline-none transition-colors duration-300 bg-transparent text-sm sm:text-base"
                    placeholder="+34 600 000 000"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Empresa
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-cyan-400 outline-none transition-colors duration-300 bg-transparent text-sm sm:text-base"
                    placeholder="Nombre de tu empresa"
                  />
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                    Sitio Web
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-cyan-400 outline-none transition-colors duration-300 bg-transparent text-sm sm:text-base"
                    placeholder="https://tusitio.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Proyecto *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-b-2 border-gray-300 focus:border-cyan-400 outline-none transition-colors duration-300 bg-transparent cursor-pointer text-sm sm:text-base"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="corporate">Sitio Corporativo</option>
                  <option value="blog">Blog / Contenido</option>
                  <option value="saas">SaaS / Plataforma</option>
                  <option value="local">Negocio Local</option>
                  <option value="other">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Mensaje *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 focus:border-cyan-400 outline-none transition-colors duration-300 rounded-xl resize-none text-sm sm:text-base"
                  placeholder="Cuéntanos sobre tu proyecto y objetivos SEO..."
                ></textarea>
                <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 caracteres
                </div>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacy"
                  required
                  className="mt-1 w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 border-gray-300 rounded focus:ring-cyan-400 cursor-pointer flex-shrink-0"
                />
                <label htmlFor="privacy" className="text-xs sm:text-sm text-gray-600">
                  Acepto la <a href="/politica-privacidad" className="text-cyan-400 hover:underline">política de privacidad</a> y el tratamiento de mis datos según la normativa GDPR
                </label>
              </div>

              <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-cyan-500 text-white rounded-lg font-medium text-lg hover:bg-cyan-600 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-400 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-check-line text-xl sm:text-2xl text-green-500"></i>
                  <p className="text-sm sm:text-base text-green-700 font-medium">¡Mensaje enviado con éxito! Te contactaremos pronto.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-error-warning-line text-xl sm:text-2xl text-red-500"></i>
                  <p className="text-sm sm:text-base text-red-700 font-medium">Error al enviar el mensaje. Por favor, inténtalo de nuevo.</p>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
